/**
 * Categories slice — system + custom categories with reference-aware delete.
 *
 * Key invariants:
 * - `is_system=true` rows can never be soft-deleted (throws
 *   `SystemCategoryError`).
 * - Before deleting a custom category, callers pass a `refCheck` that
 *   returns the number of transactions / recurring definitions referencing
 *   it. Any non-zero count → `CategoryInUseError`.
 *
 * The slice itself does not perform the reference check (it has no IO).
 * That keeps the slice pure and lets the caller (sync layer / repos)
 * implement the actual count query against Sheets.
 *
 * Slice-creator shape mirrors `sync.ts` but also threads `get` so async
 * actions can read the latest state without leaking the store handle.
 */

import type { CategoryRow } from '@/lib/sheets/types';
import type { MochiaError } from '@/lib/errors';

export interface RefCounts {
  txCount: number;
  recurringCount: number;
}

export type RefCheck = () => Promise<RefCounts>;

export class CategoryInUseError extends Error implements MochiaError {
  readonly counts: RefCounts;
  readonly hint: string;
  constructor(counts: RefCounts) {
    super(
      `Cannot delete category — referenced by ${counts.txCount} transaction(s) and ${counts.recurringCount} recurring definition(s)`,
    );
    this.name = 'CategoryInUseError';
    this.counts = counts;
    const parts: string[] = [];
    if (counts.txCount > 0) parts.push(`${counts.txCount} 筆交易`);
    if (counts.recurringCount > 0) parts.push(`${counts.recurringCount} 筆固定週期`);
    const usage = parts.join('、');
    this.hint = usage
      ? `此分類仍被 ${usage} 使用，請先軟刪除（或改分類）這些紀錄再刪除分類。`
      : '請先軟刪除使用此分類的交易與固定週期，再刪除分類。';
  }
}

export class SystemCategoryError extends Error implements MochiaError {
  readonly hint: string;
  constructor() {
    super('Cannot delete a system category');
    this.name = 'SystemCategoryError';
    this.hint = '系統分類在首次登入時建立，無法刪除。如需隱藏可從分類設定中改名或建立子分類取代用途。';
  }
}

export interface CategoriesState {
  items: CategoryRow[];
  byParent: Record<string, string[]>;
}

export interface CategoriesActions {
  setItems: (items: CategoryRow[]) => void;
  addCategory: (cat: CategoryRow) => void;
  updateCategory: (id: string, patch: Partial<CategoryRow>) => void;
  deleteCategory: (id: string, refCheck: RefCheck) => Promise<void>;
  /** v3 selector — returns rows scoped to the given book (book_id matches)
   *  PLUS the system seeds (book_id===''). Reading by `bookId === ''`
   *  yields only the system seeds. */
  categoriesForBook: (bookId: string) => CategoryRow[];
}

export type CategoriesSlice = CategoriesState & CategoriesActions;

export const initialCategoriesState: CategoriesState = {
  items: [],
  byParent: {},
};

function buildByParent(items: CategoryRow[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const c of items) {
    const k = c.parent_id || '';
    if (!k) continue;
    const list = out[k];
    if (list) list.push(c.id);
    else out[k] = [c.id];
  }
  return out;
}

export const createCategoriesSlice =
  <T extends CategoriesSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
    get?: () => T,
  ): CategoriesSlice => {
    // Track latest items locally as a fallback when `get` is not threaded
    // (e.g. plain unit tests that don't supply a real store). We mirror
    // every set into this snapshot so async actions can always read it.
    let snapshot: CategoryRow[] = [];

    const apply = (next: CategoryRow[]): Partial<T> => {
      snapshot = next;
      return { items: next, byParent: buildByParent(next) } as Partial<T>;
    };

    const readItems = (): CategoryRow[] => {
      if (get) return get().items;
      return snapshot;
    };

    return {
      ...initialCategoriesState,

      setItems: (items) => set(apply(items)),

      addCategory: (cat) =>
        set((state) => apply([...state.items, cat])),

      updateCategory: (id, patch) =>
        set((state) =>
          apply(state.items.map((c) => (c.id === id ? { ...c, ...patch } : c))),
        ),

      deleteCategory: async (id, refCheck) => {
        const items = readItems();
        const target = items.find((c) => c.id === id);
        if (!target) return;
        if (target.is_system) {
          throw new SystemCategoryError();
        }
        const counts = await refCheck();
        if (counts.txCount > 0 || counts.recurringCount > 0) {
          throw new CategoryInUseError(counts);
        }
        const now = new Date().toISOString();
        set((state) =>
          apply(
            state.items.map((c) =>
              c.id === id ? { ...c, deleted_at: now } : c,
            ),
          ),
        );
      },

      categoriesForBook: (bookId) =>
        readItems().filter((c) => c.book_id === '' || c.book_id === bookId),
    };
  };
